import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useInventory } from '../../../contexts/InventoryContext';
import SearchableSelect from '../../Common/SearchableSelect';
import SearchableCategoryDropdown from '../../Common/SearchableCategoryDropdown';
import SearchableInventorySupplierDropdown from '../../Common/SearchableInventorySupplierDropdown';
import SearchableLedgerDropdown from '../../Common/SearchableLedgerDropdown';
import { transactionLedgerMappingApi } from '../../../services/financeApi';

const ItemForm = ({ item, onSuccess, onCancel }) => {
  const { createItem, updateItem } = useInventory();

  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    category: '',
    categoryLabel: '',
    type: 'finished_goods',
    unitOfMeasure: 'PCS',
    unitPrice: '0',
    costPrice: '1',
    minStock: '',
    maxStock: '',
    reorderPoint: '',
    reorderQuantity: '',
    barcode: '',
    supplier: '',
    supplierLabel: '',
    weight: '',
    dimensions: { length: '', width: '', height: '' },
    isActive: true,
    trackSerial: false,
    trackExpiry: false,
    taxable: true,
    notes: '',
    // Finance ledger accounts (optional — blank uses the default accounts)
    inventoryAccountId: '',
    inventoryAccountLabel: '',
    expenseAccountId: '',
    expenseAccountLabel: ''
  });

  const [errors, setErrors] = useState({});
  // Default stock account from the GRN transaction ledger mapping (used when the item has none)
  const [defaultInventory, setDefaultInventory] = useState({ loading: true, isActive: false, account: null });
  useEffect(() => {
    transactionLedgerMappingApi.getInventoryDefault()
      .then(res => setDefaultInventory({ loading: false, ...(res.data?.data || {}) }))
      .catch(() => setDefaultInventory({ loading: false, isActive: false, account: null }));
  }, []);
  const defaultInventoryLabel = defaultInventory.account ? `${defaultInventory.account.code} - ${defaultInventory.account.name}` : '';
  const [submitting, setSubmitting] = useState(false);
  const [lastErrorSku, setLastErrorSku] = useState('');

  useEffect(() => {
    if (item) {
      setFormData({
        sku: item.sku || '',
        name: item.name || '',
        description: item.description || '',
        category: item.category?._id || item.category || '',
        categoryLabel: item.category?.code && item.category?.name ? `${item.category.code} - ${item.category.name}` : '',
        type: item.type || 'finished_goods',
        unitOfMeasure: item.unitOfMeasure || item.inventory?.unit || 'PCS',
        unitPrice: item.unitPrice?.toString() || item.pricing?.sellingPrice?.toString() || '0',
        costPrice: item.costPrice?.toString() || item.pricing?.unitCost?.toString() || '',
        minStock: item.inventory?.minimumStock?.toString() || item.minStock?.toString() || '',
        maxStock: item.inventory?.maximumStock?.toString() || item.maxStock?.toString() || '',
        reorderPoint: item.inventory?.reorderPoint?.toString() || item.reorderPoint?.toString() || '',
        reorderQuantity: item.inventory?.reorderQuantity?.toString() || item.reorderQuantity?.toString() || '',
        barcode: item.barcode || '',
        supplier: item.supplier?._id || item.supplier || (item.suppliers?.length > 0 ? item.suppliers[0].supplier?._id || item.suppliers[0].supplier : '') || '',
        supplierLabel: item.supplier?.code && item.supplier?.name ? `${item.supplier.code} - ${item.supplier.name}` : '',
        weight: item.weight?.toString() || item.specifications?.weight?.value?.toString() || '',
        dimensions: {
          length: item.dimensions?.length?.toString() || item.specifications?.dimensions?.length?.toString() || '',
          width: item.dimensions?.width?.toString() || item.specifications?.dimensions?.width?.toString() || '',
          height: item.dimensions?.height?.toString() || item.specifications?.dimensions?.height?.toString() || ''
        },
        isActive: item.isActive !== undefined ? item.isActive : true,
        trackSerial: item.trackSerial || item.settings?.trackSerial || false,
        trackExpiry: item.trackExpiry || item.settings?.trackExpiry || false,
        taxable: item.taxable !== undefined ? item.taxable : (item.settings?.taxable !== undefined ? item.settings.taxable : true),
        notes: item.notes || '',
        inventoryAccountId: item.accounting?.inventoryAccount?.accountId || '',
        inventoryAccountLabel: item.accounting?.inventoryAccount?.label || '',
        expenseAccountId: item.accounting?.expenseAccount?.accountId || '',
        expenseAccountLabel: item.accounting?.expenseAccount?.label || ''
      });
    }
  }, [item]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.sku.trim()) newErrors.sku = 'SKU is required';
    else if (!/^[A-Z0-9\-_]{2,50}$/.test(formData.sku)) newErrors.sku = 'SKU must be 2-50 uppercase alphanumeric characters';
    else if (errors.sku?.toLowerCase().includes('already exists') && formData.sku.toUpperCase() === lastErrorSku) newErrors.sku = errors.sku;
    if (!formData.name.trim()) newErrors.name = 'Item name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.type) newErrors.type = 'Item type is required';
    if (!formData.unitOfMeasure) newErrors.unitOfMeasure = 'Unit of measure is required';
    if (!formData.costPrice || parseFloat(formData.costPrice) <= 0) newErrors.costPrice = 'Cost price must be > 0';
    if (formData.minStock && formData.maxStock && parseFloat(formData.minStock) > parseFloat(formData.maxStock)) newErrors.maxStock = 'Max stock must be ≥ min stock';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    setErrors(prev => ({ submit: prev.submit }));
    return true;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('dimensions.')) {
      const dim = name.split('.')[1];
      setFormData(prev => ({ ...prev, dimensions: { ...prev.dimensions, [dim]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
      if (name === 'sku') setLastErrorSku('');
    }
    if (errors.submit) toast.error('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const submitData = {
        sku: formData.sku.toUpperCase(),
        name: formData.name,
        description: formData.description,
        category: formData.category,
        type: formData.type,
        inventory: {
          unit: formData.unitOfMeasure,
          minimumStock: formData.minStock ? parseFloat(formData.minStock) : undefined,
          maximumStock: formData.maxStock ? parseFloat(formData.maxStock) : undefined,
          reorderPoint: formData.reorderPoint ? parseFloat(formData.reorderPoint) : undefined,
          reorderQuantity: formData.reorderQuantity ? parseFloat(formData.reorderQuantity) : undefined
        },
        pricing: {
          sellingPrice: parseFloat(formData.unitPrice),
          unitCost: parseFloat(formData.costPrice)
        },
        specifications: {
          weight: formData.weight ? { value: parseFloat(formData.weight), unit: 'kg' } : undefined,
          dimensions: {
            length: formData.dimensions.length ? parseFloat(formData.dimensions.length) : undefined,
            width: formData.dimensions.width ? parseFloat(formData.dimensions.width) : undefined,
            height: formData.dimensions.height ? parseFloat(formData.dimensions.height) : undefined,
            unit: 'cm'
          }
        },
        settings: {
          isActive: formData.isActive,
          trackSerial: formData.trackSerial,
          trackExpiry: formData.trackExpiry,
          taxable: formData.taxable
        },
        barcode: formData.barcode || undefined,
        supplier: formData.supplier || null,
        notes: formData.notes || undefined,
        accounting: {
          inventoryAccount: { accountId: formData.inventoryAccountId || null, label: formData.inventoryAccountId ? formData.inventoryAccountLabel : '' },
          expenseAccount: { accountId: formData.expenseAccountId || null, label: formData.expenseAccountId ? formData.expenseAccountLabel : '' }
        }
      };

      Object.keys(submitData.inventory).forEach(k => {
        if (submitData.inventory[k] === undefined) delete submitData.inventory[k];
      });

      const hasDimensions = submitData.specifications.dimensions.length || submitData.specifications.dimensions.width || submitData.specifications.dimensions.height;
      if (!submitData.specifications.weight && !hasDimensions) delete submitData.specifications;
      else if (!hasDimensions) delete submitData.specifications.dimensions;

      if (item) await updateItem(item._id, submitData);
      else await createItem(submitData);
      onSuccess();
    } catch (error) {
      let msg = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to save item';
      if (msg.toLowerCase().includes('sku')) {
        setLastErrorSku(formData.sku.toUpperCase());
        setErrors(prev => ({ ...prev, sku: msg }));
        toast.error('Failed to save item — ' + msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const field = (label, name, type = 'text', opts = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{opts.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={opts.dim ? formData.dimensions[name.split('.')[1]] : formData[name]}
        onChange={handleChange}
        placeholder={opts.placeholder || ''}
        disabled={opts.disabled}
        step={opts.step}
        min={opts.min}
        className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
          errors[name] ? 'border-red-300 bg-red-50' : 'border-gray-300'
        } ${opts.disabled ? 'bg-gray-100' : ''} ${opts.right ? 'text-right' : ''}`}
      />
      {errors[name] && <p className="text-[10px] text-red-500 mt-0.5">{errors[name]}</p>}
      {opts.help && <p className="text-[10px] text-gray-400 mt-0.5">{opts.help}</p>}
    </div>
  );

  const select = (label, name, options, opts = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{opts.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <SearchableSelect
        name={name}
        value={formData[name]}
        onChange={handleChange}
        options={options}
        placeholder={`Select ${label.toLowerCase()}…`}
        disabled={opts.disabled}
        error={errors[name] || ''}
        size="sm"
      />
    </div>
  );

  const toggle = (label, name, colorClass = 'bg-indigo-600') => (
    <div className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded">
      <label htmlFor={name} className="text-xs font-medium text-gray-700 cursor-pointer select-none">{label}</label>
      <button
        type="button"
        onClick={() => setFormData(prev => ({ ...prev, [name]: !prev[name] }))}
        className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${formData[name] ? colorClass : 'bg-gray-300'}`}
      >
        <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${formData[name] ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );

  const sectionHeader = (title) => (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">{title}</span>
      <div className="flex-1 h-px bg-indigo-100" />
    </div>
  );

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-white shadow-2xl border-0 flex flex-col rounded-2xl overflow-hidden">

        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {item ? 'Edit Item' : 'Create New Item'}
                </h3>
                <p className="text-indigo-100 text-sm mt-1">
                  Manage inventory item details, pricing and stock settings
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Form Body */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="px-4 pt-3 pb-2 space-y-4">

            {/* Identity */}
            {sectionHeader('Identity')}
            <div className="grid grid-cols-4 gap-2">
              {field('SKU', 'sku', 'text', { required: true, placeholder: 'e.g. ITEM-001', disabled: !!item, help: 'Uppercase letters, numbers, dashes' })}
              {field('Item Name', 'name', 'text', { required: true, placeholder: 'e.g. Office Chair' })}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category <span className="text-red-500">*</span></label>
                <SearchableCategoryDropdown
                  value={formData.category}
                  displayLabel={formData.categoryLabel}
                  onChange={(id, label) => {
                    setFormData(prev => ({ ...prev, category: id, categoryLabel: label || '' }));
                    if (errors.category) setErrors(prev => ({ ...prev, category: '' }));
                  }}
                  placeholder="Select category…"
                  disabled={false}
                  error={!!errors.category}
                  limit={50}
                  menuPlacement="down"
                />
                {errors.category && <p className="text-[10px] text-red-500 mt-0.5">{errors.category}</p>}
              </div>
              {select('Item Type', 'type', [
                { value: 'finished_goods', label: 'Finished Good' },
                { value: 'raw_material', label: 'Raw Material' },
                { value: 'work_in_progress', label: 'Work in Progress' },
                { value: 'consumable', label: 'Consumable' },
                { value: 'service', label: 'Service' },
                { value: 'kit', label: 'Kit / Bundle' }
              ], { required: true })}
            </div>

            <div className="grid grid-cols-3 gap-2">
              {select('Unit of Measure', 'unitOfMeasure', [
                { value: 'PCS', label: 'Pieces (PCS)' },
                { value: 'KG', label: 'Kilograms (KG)' },
                { value: 'G', label: 'Grams (G)' },
                { value: 'L', label: 'Liters (L)' },
                { value: 'ML', label: 'Milliliters (ML)' },
                { value: 'M', label: 'Meters (M)' },
                { value: 'CM', label: 'Centimeters (CM)' },
                { value: 'SET', label: 'Set (SET)' },
                { value: 'BOX', label: 'Box (BOX)' },
                { value: 'PACK', label: 'Pack (PACK)' },
                { value: 'DOZEN', label: 'Dozen (DOZ)' },
                { value: 'PAIR', label: 'Pair (PAIR)' }
              ], { required: true })}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Preferred Supplier</label>
                <SearchableInventorySupplierDropdown
                  value={formData.supplier}
                  displayLabel={formData.supplierLabel}
                  onChange={(id, label) => {
                    setFormData(prev => ({ ...prev, supplier: id, supplierLabel: label || '' }));
                    if (errors.supplier) setErrors(prev => ({ ...prev, supplier: '' }));
                  }}
                  placeholder="Select supplier…"
                  disabled={false}
                  error={!!errors.supplier}
                  limit={50}
                  menuPlacement="down"
                />
                {errors.supplier && <p className="text-[10px] text-red-500 mt-0.5">{errors.supplier}</p>}
              </div>
              {field('Barcode', 'barcode', 'text', { placeholder: 'Scan or enter barcode', help: 'Leave empty to auto-generate' })}
            </div>

            {/* Pricing */}
            {sectionHeader('Pricing')}
            <div className="grid grid-cols-2 gap-2">
              {field('Selling Price', 'unitPrice', 'number', { placeholder: '0.00', step: '0.01', min: '0', right: true, disabled: true, help: 'Set via pricing module' })}
              {field('Cost Price', 'costPrice', 'number', { required: true, placeholder: '0.00', step: '0.01', min: '0', right: true, help: 'Purchase / manufacturing cost' })}
            </div>

            {/* Accounting */}
            {sectionHeader('Accounting')}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Inventory Account</label>
                <SearchableLedgerDropdown
                  value={formData.inventoryAccountId}
                  displayLabel={formData.inventoryAccountLabel}
                  ledgerGroup="ASSETS"
                  placeholder={defaultInventoryLabel ? `Default: ${defaultInventoryLabel}` : 'Default (GRN ledger mapping)'}
                  onChange={(id, label) => setFormData(prev => ({ ...prev, inventoryAccountId: id || '', inventoryAccountLabel: label || '' }))}
                  menuPlacement="down"
                />
                {formData.inventoryAccountId ? (
                  <p className="mt-0.5 text-[11px] text-indigo-700">
                    Posts to <span className="font-medium">{formData.inventoryAccountLabel}</span> (this item){defaultInventoryLabel ? ` instead of the default ${defaultInventoryLabel}` : ''}
                  </p>
                ) : defaultInventory.loading ? (
                  <p className="mt-0.5 text-[11px] text-gray-400">Loading default account…</p>
                ) : defaultInventoryLabel ? (
                  <p className="mt-0.5 text-[11px] text-gray-500">
                    Posts to <span className="font-medium text-gray-700">{defaultInventoryLabel}</span> — GRN transaction ledger mapping{defaultInventory.isActive ? '' : ' (mapping switched off — GRNs are not posted)'}
                  </p>
                ) : (
                  <p className="mt-0.5 text-[11px] text-amber-700">No stock account set in the GRN transaction ledger mapping — set one there or choose an account here</p>
                )}
                <p className="text-[11px] text-gray-400">Debited on GRN, credited on returns and issues</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Expense Account</label>
                <SearchableLedgerDropdown
                  value={formData.expenseAccountId}
                  displayLabel={formData.expenseAccountLabel}
                  ledgerGroup="EXPENSES"
                  placeholder="Default (chosen when issuing)"
                  onChange={(id, label) => setFormData(prev => ({ ...prev, expenseAccountId: id || '', expenseAccountLabel: label || '' }))}
                  menuPlacement="down"
                />
                {formData.expenseAccountId ? (
                  <p className="mt-0.5 text-[11px] text-indigo-700">Posts to <span className="font-medium">{formData.expenseAccountLabel}</span> (this item)</p>
                ) : (
                  <p className="mt-0.5 text-[11px] text-gray-500">Posts to the expense account chosen when the stock is issued</p>
                )}
                <p className="text-[11px] text-gray-400">Debited when the item is consumed / issued</p>
              </div>
            </div>

            {/* Stock Levels */}
            {sectionHeader('Stock Levels')}
            <div className="grid grid-cols-4 gap-2">
              {field('Min Stock', 'minStock', 'number', { placeholder: '0', min: '0' })}
              {field('Max Stock', 'maxStock', 'number', { placeholder: '0', min: '0' })}
              {field('Reorder Point', 'reorderPoint', 'number', { placeholder: '0', min: '0' })}
              {field('Reorder Qty', 'reorderQuantity', 'number', { placeholder: '0', min: '0' })}
            </div>

            {/* Physical Specs */}
            {sectionHeader('Physical Specs')}
            <div className="grid grid-cols-4 gap-2">
              {field('Weight (kg)', 'weight', 'number', { placeholder: '0.000', step: '0.001', min: '0' })}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Length (cm)</label>
                <input type="number" name="dimensions.length" value={formData.dimensions.length} onChange={handleChange} placeholder="0" min="0" step="0.1"
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Width (cm)</label>
                <input type="number" name="dimensions.width" value={formData.dimensions.width} onChange={handleChange} placeholder="0" min="0" step="0.1"
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Height (cm)</label>
                <input type="number" name="dimensions.height" value={formData.dimensions.height} onChange={handleChange} placeholder="0" min="0" step="0.1"
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
            </div>

            {/* Description & Notes */}
            {sectionHeader('Notes')}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Brief description of the item…"
                  className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none ${errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">{formData.description.length}/1000</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Internal Notes</label>
                <textarea
                  name="notes"
                  rows={3}
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Internal notes…"
                  className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none ${errors.notes ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">{formData.notes.length}/500</p>
              </div>
            </div>

            {/* Settings */}
            {sectionHeader('Settings')}
            <div className="grid grid-cols-4 gap-2">
              {toggle('Active Item', 'isActive', 'bg-indigo-600')}
              {toggle('Track Serial Numbers', 'trackSerial', 'bg-purple-600')}
              {toggle('Track Expiry Date', 'trackExpiry', 'bg-orange-500')}
              {toggle('Taxable Item', 'taxable', 'bg-green-600')}
            </div>

            {errors.submit && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded px-3 py-2">{errors.submit}</p>
            )}

          </form>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-white px-6 py-4 border-t border-gray-200 shadow-lg">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <button
              type="button"
              onClick={onCancel}
              className="px-8 py-3 border border-gray-300 rounded-xl text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-3 bg-indigo-600 border border-transparent rounded-xl text-base font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
            >
              {submitting ? (
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Saving...</span>
                </div>
              ) : (
                item ? 'Update Item' : 'Create Item'
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ItemForm;
